import { makeStyles } from '@material-ui/styles'
import { getFieldsForCard } from 'core/components/InfoPanel'
import Progress from 'core/components/progress/Progress'
import Card from 'core/elements/card'
import Text from 'core/elements/Text'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import Theme from 'core/themes/model'
import InfoCard from 'k8s/components/common/entity/info-card'
import { prop } from 'ramda'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { getResmgrRoleConfig, getResources, listResourceInventory } from '../../actions'
import { hasHypervisorRole } from '../../helpers'
import {
  CPU_ALLOCATION_RATIO_TOOLTIP,
  EPHEMERAL_DISK_ALLOCATION_RATIO_TOOLTIP,
  MEMORY_ALLOCATION_RATIO_TOOLTIP,
} from '../helpers'

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  overview: {
    display: 'grid',
    gridTemplateColumns: '633px 1fr',
    marginTop: '16px',
    gridGap: '24px',
  },
  column: {
    display: 'grid',
    gridGap: '24px',
    gridAutoFlow: 'row',
    gridAutoRows: 'max-content',
  },
  interfaces: {
    display: 'grid',
    gap: 20,
    gridTemplateColumns: 'max-content auto',
  },
}))

const ALLOCATION_RATIO_KEYS = {
  VCPU: {
    label: 'CPU Allocation Ratio',
    info: CPU_ALLOCATION_RATIO_TOOLTIP,
  },
  MEMORY_MB: {
    label: 'Memory Allocation Ratio',
    info: MEMORY_ALLOCATION_RATIO_TOOLTIP,
  },
  DISK_GB: {
    label: 'Ephemeral Disk Allocation Ratio',
    info: EPHEMERAL_DISK_ALLOCATION_RATIO_TOOLTIP,
  },
}

const cpuInfoFields = [
  {
    id: 'cpu_info.cpu_vendor',
    title: 'Vendor',
  },
  {
    id: 'cpu_info.cpu_model.model_name',
    title: 'Model',
  },
  {
    id: 'arch',
    title: 'Architecture',
  },
  {
    id: 'cpu_info.cpu_cores',
    title: 'Cores',
  },
  {
    id: 'cpu_info.cpu_threads.total',
    title: 'Threads',
  },
  {
    id: 'cpu_info.cpu_sockets',
    title: 'Sockets',
  },
  {
    id: 'cpu_info.cpu_features',
    title: 'Features',
    render: (features) => features.join(', '),
  },
]

const propertyFields = [
  {
    id: 'name',
    title: 'Name',
  },
  {
    id: 'id',
    title: 'ID',
  },
  {
    id: 'info.os_info',
    title: 'Operating System',
  },
  {
    id: 'displayRoles',
    title: 'Roles',
    render: (roles) => roles.join(', '),
  },
]

export default function Overview({ host, loading }) {
  const classes = useStyles({})
  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { features } = session
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'
  const [pathOnHost, setPathOnHost] = useState('')
  const [allocationRatio, setAllocationRatio] = useState([])
  const [isLoading, setIsLoading] = useState(loading)

  const cpuInfo = useMemo(() => {
    return getFieldsForCard(cpuInfoFields, host?.info)
  }, [host])

  useEffect(() => {
    const getAllocationRatios = async () => {
      const resources = await getResources()
      const hostInventoryDetails = resources.filter((resource) => resource.name === host.name)

      try {
        const { inventories } = await listResourceInventory({ id: hostInventoryDetails?.[0]?.uuid })

        if (inventories) {
          const filteredRatios = Object.keys(inventories).map((obj: any) => {
            const config = ALLOCATION_RATIO_KEYS[obj]
            return {
              [config.label]: {
                ...inventories[obj],
                value: inventories[obj].allocation_ratio,
                info: config.info,
              },
            }
          })

          setAllocationRatio(filteredRatios)
        }
        setIsLoading(false)
      } catch (e) {
        setIsLoading(false)
      }
    }

    getAllocationRatios()
  }, [host])

  useEffect(() => {
    const getHypervisorInfo = async () => {
      const response: any = await getResmgrRoleConfig({
        role: isVmware ? 'pf9-ostackhost-neutron-vmw' : 'pf9-ostackhost-neutron',
        host: { id: host?.id },
      })
      setPathOnHost(response?.instances_path)
    }
    if (hasHypervisorRole(host)) {
      getHypervisorInfo()
    }
  }, [host, isVmware])

  const propInfo = useMemo(() => {
    const getPropertyField = { ...getFieldsForCard(propertyFields, host) }
    const pathInfo = pathOnHost
      ? {
          'VM Storage Path': {
            value: pathOnHost,
          },
        }
      : {}
    return Object.assign(getPropertyField, ...allocationRatio, pathInfo)
  }, [host, allocationRatio, pathOnHost])

  return (
    <Progress loading={loading || isLoading}>
      <div className={classes.overview}>
        <div className={classes.column}>
          <InfoCard items={propInfo} title="Properties" />
          <Card title="Network Interfaces">
            <div className={classes.interfaces}>
              {host?.interfaces?.map((iface) => (
                <>
                  <Text variant="body2">{iface.name}</Text>
                  <Text variant="caption1">{iface.ip}</Text>
                </>
              ))}
            </div>
          </Card>
        </div>
        <div className={classes.column}>
          <InfoCard items={cpuInfo} title="CPU Info" />
        </div>
      </div>
    </Progress>
  )
}
